import React, { ReactNode } from "react";
import { styled } from "@mui/system";
import MUIAlert, { AlertProps } from "@mui/material/Alert";
import Modal from "@vapor/react-material/Modal";
import Stack from "@vapor/react-material/Stack";
import Box from "@vapor/react-material/Box";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faMinusCircle } from "@fortawesome/free-solid-svg-icons/faMinusCircle";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";

declare module "@mui/material/Alert" {
  interface AlertPropsVariantOverrides {
    overflow: true;
  }
}

export type VaporAlertProps = {
  leftButtons?: React.ReactNode[] | React.ReactNode;
  rightButtons?: React.ReactNode[] | React.ReactNode;
  severity?: "success" | "info" | "warning" | "error";
} & AlertProps;

export const VaporAlert: (props: VaporAlertProps) => JSX.Element = styled(
  React.forwardRef<HTMLDivElement, VaporAlertProps>((props, ref) => {
    const {
      leftButtons,
      rightButtons,
      severity = "success",
      children,
      ...otherProps
    } = props;

    return (
      <>
        <Modal
          open
          sx={{
            position: "absolute",
            bottom: 64,
            top: "unset",
            left: 34,
            right: 34
          }}
        >
          <MUIAlert
            icon={
              <FontAwesomeIcon
                icon={
                  {
                    success: faCheckCircle,
                    warning: faExclamationCircle,
                    info: faInfoCircle,
                    error: faMinusCircle
                  }[severity]
                }
              />
            }
            severity={severity}
            ref={ref}
            {...otherProps}
          >
            {children}
            {(leftButtons !== undefined || rightButtons !== undefined) && (
              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 3, justifyContent: "space-between" }}
              >
                <Box>
                  {leftButtons !== undefined && Array.isArray(leftButtons) ? (
                    leftButtons.map((button, index) => (
                      <React.Fragment key={index}>{button}</React.Fragment>
                    ))
                  ) : (
                    <>{leftButtons}</>
                  )}
                </Box>
                <Box>
                  {rightButtons !== undefined && Array.isArray(rightButtons) ? (
                    rightButtons.map((button, index) => (
                      <React.Fragment key={index}>{button}</React.Fragment>
                    ))
                  ) : (
                    <>{rightButtons}</>
                  )}
                </Box>
              </Stack>
            )}
          </MUIAlert>
        </Modal>
      </>
    );
  }),
  {
    name: "VaporAlert",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)(({ theme, variant }) => ({
  "&.MuiAlert-root": {
    ...theme.typography["body"],
    boxShadow: variant === "overflow" && theme.shadows[5],
    fontSize: 14,
    marginTop: 16,
    marginBottom: 16,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 3.5,
    paddingBottom: 3.5,
    "& .MuiAlert-message": {
      paddingTop: 8,
      paddingBottom: 8,
      paddingLeft: 0,
      paddingRight: 0,
      width: "100%"
    },
    "& .MuiAlert-icon": {
      fontSize: 18,
      paddingTop: 8
    },
    "& .MuiAlert-action .MuiButtonBase-root .MuiSvgIcon-root": {
      color: theme.palette.primary.iconDefault
    }
  },

  "&.MuiAlert-overflowError": {
    border: "1px solid" + theme.palette.primary.baseCriticalColor,
    borderBottom: "4px solid" + theme.palette.primary.baseCriticalColor,
    "& .MuiAlert-icon": {
      color: theme.palette.primary.baseCriticalColor
    }
  },
  "&.MuiAlert-overflowInfo": {
    border: "1px solid" + theme.palette.primary.baseInfoColor,
    borderBottom: "4px solid" + theme.palette.primary.baseInfoColor,
    "& .MuiAlert-icon": {
      color: theme.palette.primary.baseInfoColor
    }
  },
  "&.MuiAlert-overflowSuccess": {
    border: "1px solid" + theme.palette.primary.borderSuccessed,
    borderBottom: "4px solid" + theme.palette.primary.borderSuccessed,
    "& .MuiAlert-icon": {
      color: theme.palette.primary.borderSuccessed
    }
  },
  "&.MuiAlert-overflowWarning": {
    border: "1px solid" + theme.palette.primary.baseWarningColor,
    borderBottom: "4px solid" + theme.palette.primary.baseWarningColor,
    "& .MuiAlert-icon": {
      color: theme.palette.primary.baseWarningColor
    }
  }
}));
